import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Table,  Badge } from 'reactstrap';
import Chart from 'react-apexcharts';
import { Breadcrumbs } from '../../AbstractElements';
import Button from '../Common/Elements/Button';
import { useSelector } from 'react-redux';
import { getClientUrlDataToken } from '../../Utils/restUtils';
import { getAttendanceCounts, getAttendanceCountsRole, getTodaysAttendanceReport, getTodaysAttendanceReportRole, getTodaysBreakdown, getTodaysBreakdownRole, getWeeklyAttendanceTrend, getWeeklyAttendanceTrendRole } from '../../CommonUrl';
import Transparentspinner from '../Common/Loader/Transparentspinner';

function DashBoard() {
  const authState = useSelector((state) => state.auth);
  const [countsData, setCountsData] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [weeklyTrendData, setWeeklyTrendData] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getAvatarBg = (name) => {
    const colors = ['#0f766e', '#7c3aed', '#0284c7', '#ea580c', '#dc2626', '#2563eb', '#059669', '#d97706'];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getStatusClass = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('present')) return 'badge-approved';
    if (s.includes('late')) return 'badge-sent';
    if (s.includes('leave')) return 'badge-negotiation';
    if (s.includes('absent')) return 'badge-rejected';
    return 'badge-light';
  };

  const fetchCounts = async () => {
    if (!authState.apiUrl) return;
    try {
      const endpoint = authState.userRole === 1 ? getAttendanceCounts : getAttendanceCountsRole;
      const response = await getClientUrlDataToken(authState.apiUrl, endpoint);
      if (response && response.status === true) {
        setCountsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching attendance counts:", error);
    }
  };

  const fetchBreakdown = async () => {
    if (!authState.apiUrl) return;
    try {
      const endpoint = authState.userRole === 1 ? getTodaysBreakdown : getTodaysBreakdownRole;
      const response = await getClientUrlDataToken(authState.apiUrl, endpoint);
      if (response && response.status === true) {
        setBreakdownData(response.data);
      }
    } catch (error) {
      console.error("Error fetching today's breakdown:", error);
    }
  };

  const fetchWeeklyAttendanceTrend = async () => {
    if (!authState.apiUrl) return;
    try {
      const endpoint = authState.userRole === 1 ? getWeeklyAttendanceTrend : getWeeklyAttendanceTrendRole;
      const response = await getClientUrlDataToken(authState.apiUrl, endpoint);
      if (response && response.status === true) {
        setWeeklyTrendData(response.data);
      }
    } catch (error) {
      console.error("Error fetching weekly attendance trend:", error);
    }
  };

  const fetchTodaysAttendanceReport = async () => {
    if (!authState.apiUrl) return;
    try {
      const endpoint = authState.userRole === 1 ? getTodaysAttendanceReport : getTodaysAttendanceReportRole;
      const response = await getClientUrlDataToken(authState.apiUrl, endpoint);
      if (response && response.status === true) {
        let reportData = [];
        if (Array.isArray(response.data)) {
          reportData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          reportData = response.data.data;
        }

        const transformed = reportData.map((item) => {
          const name = item.employee_name || item.name || item.user_name || 'N/A';
          const empId = item.employee_id || item.empId || item.emp_id || 'N/A';
          const dept = item.department_name || item.department || item.dept || 'N/A';
          const checkIn = item.checkin_time || item.in || item.in_time || '—';
          const checkOut = item.checkout_time || item.out || item.out_time || '—';
          const hours = item.formatted_hours || item.total_hours || item.hours || '—';
          const status = item.status || 'N/A';

          return {
            initials: getInitials(name),
            avatarBg: getAvatarBg(name),
            name,
            empId,
            dept,
            in: checkIn,
            out: checkOut,
            hours,
            status,
            className: getStatusClass(status)
          };
        });
        setAttendanceData(transformed);
      }
    } catch (error) {
      console.error("Error fetching today's attendance report:", error);
    }
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCounts(),
        fetchTodaysAttendanceReport(),
        fetchBreakdown(),
        fetchWeeklyAttendanceTrend()
      ]);
      setLoading(false);
    };
    loadDashboardData();
  }, []);

  const totalEmployees = countsData ? (countsData.total_employees || countsData.totalEmployees || countsData.total || 0) : 124;
  const presentToday = countsData ? (countsData.present_today || countsData.presentToday || countsData.present || 0) : 98;
  const onLeave = countsData ? (countsData.on_leave || countsData.onLeave || countsData.leave || 0) : 14;
  const absent = countsData ? (countsData.absent || countsData.absent_today || countsData.absentToday || 0) : 12;
  const lateArrivals = countsData ? (countsData.late_arrivals || countsData.lateArrivals || countsData.late || 0) : 6;

  const totalEmployeesSubtext = countsData?.total_employees_subtext || countsData?.totalEmployeesSubtext || '+4 this month';
  const onLeaveSubtext = countsData?.on_leave_subtext || countsData?.onLeaveSubtext || '8 approved · 6 pending';
  const absentSubtext = countsData?.absent_subtext || countsData?.absentSubtext || '-2 vs yesterday';
  const lateArrivalsSubtext = countsData?.late_arrivals_subtext || countsData?.lateArrivalsSubtext || 'Avg 18 min late';

  const attendanceRate = totalEmployees > 0 ? ((presentToday / totalEmployees) * 100).toFixed(1) : '0.0';
  // Default values for Weekly Trend Chart
  let trendCategories = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  let trendPresent = [95, 98, 92, 100, 90, 30, 20];
  let trendLeave = [10, 5, 12, 4, 15, 5, 2];
  let trendAbsent = [5, 5, 8, 3, 10, 2, 0];

  if (weeklyTrendData) {
    if (Array.isArray(weeklyTrendData)) {
      trendCategories = weeklyTrendData.map(item => item.day || item.date || '');
      trendPresent = weeklyTrendData.map(item => item.present ?? item.today_present ?? item.present_count ?? 0);
      trendLeave = weeklyTrendData.map(item => item.leave ?? item.leave_count ?? item.on_leave ?? 0);
      trendAbsent = weeklyTrendData.map(item => item.absent ?? item.absent_count ?? 0);
    } else if (typeof weeklyTrendData === 'object') {
      if (Array.isArray(weeklyTrendData.categories || weeklyTrendData.days)) {
        trendCategories = weeklyTrendData.categories || weeklyTrendData.days;
      }
      if (Array.isArray(weeklyTrendData.present)) {
        trendPresent = weeklyTrendData.present;
      } else if (Array.isArray(weeklyTrendData.today_present)) {
        trendPresent = weeklyTrendData.today_present;
      }
      if (Array.isArray(weeklyTrendData.leave)) {
        trendLeave = weeklyTrendData.leave;
      } else if (Array.isArray(weeklyTrendData.leave_count)) {
        trendLeave = weeklyTrendData.leave_count;
      }
      if (Array.isArray(weeklyTrendData.absent)) {
        trendAbsent = weeklyTrendData.absent;
      } else if (Array.isArray(weeklyTrendData.absent_count)) {
        trendAbsent = weeklyTrendData.absent_count;
      }
    }
  }

  const barChartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      stacked: false,
    },
    colors: ['#059669', '#d97706', '#dc2626'],
    plotOptions: {
      bar: {
        columnWidth: '45%',
        borderRadius: 2,
      }
    },
    dataLabels: { enabled: false },
    stroke: { width: 0, colors: ['transparent'] },
    xaxis: {
      categories: trendCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: '#9ca3af', fontWeight: 500 } }
    },
    yaxis: { show: false },
    grid: {
      yaxis: { lines: { show: false } },
      padding: { bottom: -10 }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      markers: { radius: 2, width: 12, height: 12 },
      itemMargin: { horizontal: 10, vertical: 10 }
    }
  };

  const barChartSeries = [
    { name: 'Present', data: trendPresent },
    { name: 'Leave', data: trendLeave },
    { name: 'Absent', data: trendAbsent }
  ];

  const donutChartOptions = {
    chart: { type: 'donut', sparkline: { enabled: true } },
    colors: ['#059669', '#d97706', '#dc2626', '#2563eb'],
    labels: ['Present', 'Leave', 'Absent', 'Late'],
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    plotOptions: {
      pie: {
        donut: {
          size: '75%',
          labels: {
            show: true,
            name: { show: true, fontSize: '12px', color: '#6b7280', offsetY: 15 },
            value: { show: true, fontSize: '22px', fontWeight: 700, color: '#1f2937', offsetY: -5, formatter: () => '79%' },
            total: {
              show: true,
              showAlways: true,
              // label: 'present',
              fontSize: '12px',
              fontWeight: 500,
              color: '#6b7280',
              formatter: function (w) {
                // return '79%'
              }
            }
          }
        }
      }
    },
    legend: { show: false }
  };

  const recentActivities = [
    { iconBg: '#e6f4ea', title: 'Arjun Menon clocked in', time: '9:04 AM', desc: 'BillerQ Office · GPS verified' },
    { iconBg: '#fef3c7', title: 'Leave request — Priya Suresh', time: '8:47 AM', desc: 'Annual leave · 20-21 May' },
    { iconBg: '#fee2e2', title: 'Geofence violation flagged', time: '8:30 AM', desc: 'Suresh P · 800m from zone' },
    { iconBg: '#dbeafe', title: 'April payroll processed', time: 'Yesterday', desc: '124 employees · ₹38.4L total' },
    { iconBg: '#e6f4ea', title: 'New employee onboarded', time: 'May 13', desc: 'Kavitha R · Finance dept.' },
  ];

  const pendingApprovals = [
    { initials: 'PS', avatarBg: '#7c3aed', name: 'Priya Suresh', desc: 'Annual Leave · 20 May - 21 May · 2 days' },
    { initials: 'MN', avatarBg: '#ea580c', name: 'Meena Nair', desc: 'Attendance Correction · May 6 · Missing clock-out' },
    { initials: 'RK', avatarBg: '#0f766e', name: 'Rahul Kumar', desc: 'Overtime Pre-approval · May 17 · Est. 3h OT' },
  ];

  const presentVal = breakdownData?.present || 0;
  const leaveVal = breakdownData?.leave_count || 0;
  const lateVal = breakdownData?.late_count || 0;

  const donutChartSeries = [presentVal, leaveVal, lateVal];

  return (
    <>
      {loading && <Transparentspinner />}
      <Breadcrumbs mainTitle={'Attendance'} parent="Dashboard" title={'Attendance'} />

      <>
        <>

          {/* Top Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Total Employees</span>
                  <div style={{ width: '32px', height: '32px', backgroundColor: '#eff6ff', borderRadius: '8px' }}></div>
                </div>
                <h2 style={{ fontWeight: '700', marginBottom: '4px', color: '#111827', fontSize: '28px' }}>{countsData?.employee_count}</h2>
                {/* <span style={{ fontSize: '13px', color: '#059669', fontWeight: '600' }}>{totalEmployeesSubtext}</span> */}
              </CardBody>
            </Card>
            <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Present Today</span>
                  <div style={{ width: '32px', height: '32px', backgroundColor: '#ecfdf5', borderRadius: '8px' }}></div>
                </div>
                <h2 style={{ fontWeight: '700', marginBottom: '4px', color: '#059669', fontSize: '28px' }}>{countsData?.today_present}</h2>
                {/* <span style={{ fontSize: '13px', color: '#6b7280' }}>{attendanceRate}% attendance rate</span> */}
              </CardBody>
            </Card>
            <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>On Leave</span>
                  <div style={{ width: '32px', height: '32px', backgroundColor: '#fef3c7', borderRadius: '8px' }}></div>
                </div>
                <h2 style={{ fontWeight: '700', marginBottom: '4px', color: '#d97706', fontSize: '28px' }}>{countsData?.leave_count}</h2>
                {/* <span style={{ fontSize: '13px', color: '#6b7280' }}>{}</span> */}
              </CardBody>
            </Card>
            {/* <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Absent / No-show</span>
                  <div style={{ width: '32px', height: '32px', backgroundColor: '#fee2e2', borderRadius: '8px' }}></div>
                </div>
                <h2 style={{ fontWeight: '700', marginBottom: '4px', color: '#dc2626', fontSize: '28px' }}>{absent}</h2>
                <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>{absentSubtext}</span>
              </CardBody>
            </Card> */}
            <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Late Arrivals</span>
                  <div style={{ width: '32px', height: '32px', backgroundColor: '#dbeafe', borderRadius: '8px' }}></div>
                </div>
                <h2 style={{ fontWeight: '700', marginBottom: '4px', color: '#2563eb', fontSize: '28px' }}>{countsData?.late_count}</h2>
                {/* <span style={{ fontSize: '13px', color: '#6b7280' }}>{lateArrivalsSubtext}</span> */}
              </CardBody>
            </Card>
          </div>

          {/* Charts Row */}
          <Row className="mb-4">
            <Col lg={8} className="mb-4 mb-lg-0">
              <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '100%' }}>
                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center pt-4 pb-0">
                  <h5 style={{ fontWeight: '700', color: '#111827', margin: 0, fontSize: '16px' }}>Weekly Attendance Trend</h5>
                  <div className="d-flex align-items-center">
                    <select className="form-select form-select-sm border border-gray-300 me-3 shadow-none outline-none" style={{ backgroundColor: '#fff', fontWeight: '500', color: '#374151', borderRadius: '6px', cursor: 'pointer' }}>
                      <option>Last 7 days</option>
                    </select>
                    <span style={{ color: '#059669', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>Export</span>
                  </div>
                </CardHeader>
                <CardBody>
                  <Chart options={barChartOptions} series={barChartSeries} type="bar" height={250} />
                </CardBody>
              </Card>
            </Col>
            <Col lg={4}>
              <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '100%' }}>
                <CardHeader className="bg-white border-0 pt-4 pb-0">
                  <h5 style={{ fontWeight: '700', color: '#111827', margin: 0, fontSize: '16px' }}>Today's Breakdown</h5>
                </CardHeader>
                <CardBody className="d-flex align-items-center  p-0">
                  <Row className=" d-flex  justify-content-center w-100 m-0">
                    <Col xs={7} className="d-flex justify-content-center ">
                      <div style={{ width: '140px', height: '140px' }}>
                        <Chart options={donutChartOptions} series={donutChartSeries} type="donut" height="100%" />
                      </div>
                    </Col>
                    <Col xs={5}>
                      <div className="d-flx flex-column justify-content-end" style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>
                        <div className="d-flex justify-content-between mb-3 ">
                          <span className="d-flex align-items-center"><span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#059669', marginRight: '8px' }}></span>Present</span>
                          <span style={{ color: '#111827', fontWeight: '700' }}>{presentVal}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-3 align-items-center">
                          <span className="d-flex align-items-center"><span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d97706', marginRight: '8px' }}></span>Leave</span>
                          <span style={{ color: '#111827', fontWeight: '700' }}>{leaveVal}</span>
                        </div>
                       
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="d-flex align-items-center"><span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#2563eb', marginRight: '8px' }}></span>Late</span>
                          <span style={{ color: '#111827', fontWeight: '700' }}>{lateVal}</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Live Attendance & Recent Activity */}
          <Row className="mb-4">
            <Col className="mb-4 mb-lg-0">
              <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '100%' }}>
                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center pt-4 pb-3 border-bottom" style={{ borderBottomColor: '#f3f4f6' }}>
                  <h5 style={{ fontWeight: '700', color: '#111827', margin: 0, fontSize: '16px' }}>Today's Attendance · Live</h5>
                  <div className="d-flex align-items-center">
                    <span style={{ color: '#059669', fontWeight: '600', fontSize: '14px', marginRight: '15px', display: 'flex', alignItems: 'center' }}>
                      <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#059669', marginRight: '6px' }}></span> Live
                    </span>
                    <span style={{ color: '#059669', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>View all</span>
                  </div>
                </CardHeader>
                <CardBody className="p-0">
                  <Table responsive className="mb-0" style={{ fontSize: '13px' }}>
                    <thead style={{ backgroundColor: '#f9fafb', color: '#6b7280', fontWeight: '600' }}>
                      <tr>
                        <th className="border-0 px-4 py-3" style={{ fontWeight: '600' }}>EMPLOYEE</th>
                        <th className="border-0 py-3" style={{ fontWeight: '600' }}>DEPT</th>
                        <th className="border-0 py-3" style={{ fontWeight: '600' }}>IN</th>
                        <th className="border-0 py-3" style={{ fontWeight: '600' }}>OUT</th>
                        <th className="border-0 py-3" style={{ fontWeight: '600' }}>HOURS</th>
                        <th className="border-0 py-3" style={{ fontWeight: '600' }}>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceData.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted" style={{ fontSize: '14px' }}>
                            No attendance records found for today.
                          </td>
                        </tr>
                      ) : (
                        attendanceData.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: idx === attendanceData.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
                            <td className="px-4 py-3 d-flex align-items-center">
                              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: row.avatarBg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', marginRight: '12px', fontSize: '14px' }}>
                                {row.initials}
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{row.name}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.empId}</div>
                              </div>
                            </td>
                            <td className="py-3 align-middle" style={{ color: '#4b5563' }}>{row.dept}</td>
                            <td className="py-3 align-middle" style={{ color: '#4b5563' }}>{row.in}</td>
                            <td className="py-3 align-middle" style={{ color: '#4b5563' }}>{row.out}</td>
                            <td className="py-3 align-middle" style={{ color: '#4b5563' }}>{row.hours}</td>
                            <td className="py-3 align-middle">
                              <span className={`${row.className}`} >
                                {row.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
            {/* <Col lg={4}>
              <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '100%' }}>
                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center pt-4 pb-3 border-bottom" style={{ borderBottomColor: '#f3f4f6' }}>
                  <h5 style={{ fontWeight: '700', color: '#111827', margin: 0, fontSize: '16px' }}>Recent Activity</h5>
                  <span style={{ color: '#059669', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>View all</span>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="list-group list-group-flush">
                    {recentActivities.map((act, idx) => (
                      <div key={idx} className="list-group-item border-0 py-3 px-4 d-flex align-items-start" style={{ borderBottom: idx === recentActivities.length - 1 ? 'none' : '1px solid #f3f4f6', backgroundColor: 'transparent' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: act.iconBg, marginRight: '15px', flexShrink: 0 }}></div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span style={{ fontWeight: '600', color: '#111827', fontSize: '13px' }}>{act.title}</span>
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>{act.time}</span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{act.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col> */}
          </Row>

          {/* Pending Approvals */}
          {/* <Row>
            <Col md={12}>
              <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center pt-4 pb-3 border-bottom" style={{ borderBottomColor: '#f3f4f6' }}>
                  <h5 style={{ fontWeight: '700', color: '#111827', margin: 0, fontSize: '16px' }}>Pending Approvals</h5>
                  <span style={{ color: '#059669', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>View all (3)</span>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="list-group list-group-flush">
                    {pendingApprovals.map((app, idx) => (
                      <div key={idx} className="list-group-item border-0 py-3 px-4 d-flex align-items-center justify-content-between" style={{ borderBottom: idx === pendingApprovals.length - 1 ? 'none' : '1px solid #f3f4f6', backgroundColor: 'transparent' }}>
                        <div className="d-flex align-items-center">
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: app.avatarBg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', marginRight: '15px', fontSize: '14px' }}>
                            {app.initials}
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{app.name}</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>{app.desc}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span className='badge-negotiation'>
                            Pending
                          </span>
                          <Button className='button-approved'>Approve</Button>
                          <Button className='button-rejected'>Reject</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row> */}
        </>

      </>
    </>
  );
}

export default DashBoard;